import { useCallback, useEffect, useState } from "react";

export type Page =
	| "dashboard"
	| "calls"
	| "outbound-playbooks"
	| "playbook-runs"
	| "services"
	| "agents"
	| "settings"
	| "register";

interface RouterState {
	page: Page;
	callId: string | null;
	agentName: string | null;
}

export function useRouter() {
	const [state, setState] = useState<RouterState>(() => {
		const params = new URLSearchParams(window.location.search);
		let page = (params.get("page") as Page) || "dashboard";
		if (window.location.pathname === "/register") {
			page = "register";
		}
		const callId = params.get("callId");
		const agentName = params.get("agentName");

		return {
			page,
			callId,
			agentName,
		};
	});

	const updateUrl = useCallback((newState: RouterState) => {
		if (newState.page === "register") {
			window.history.pushState(newState, "", "/register");
			return;
		}

		const params = new URLSearchParams();
		if (newState.page !== "dashboard") {
			params.set("page", newState.page);
		}

		if (newState.callId) {
			params.set("callId", newState.callId);
		}

		if (newState.agentName) {
			params.set("agentName", newState.agentName);
		}

		const newUrl = params.toString() ? `?${params.toString()}` : "/";
		window.history.pushState(newState, "", newUrl);
	}, []);

	const setPage = useCallback(
		(page: Page) => {
			setState((prev) => {
				const next = { ...prev, page, callId: null, agentName: null };
				updateUrl(next);
				return next;
			});
		},
		[updateUrl],
	);

	const navigateToCall = useCallback(
		(callId: string | null | undefined) => {
			setState((prev) => {
				// If closing a call, stay on calls page
				const next = { ...prev, page: "calls" as Page, callId: callId || null };
				updateUrl(next);
				return next;
			});
		},
		[updateUrl],
	);

	const navigateToAgent = useCallback(
		(agentName: string | null | undefined) => {
			setState((prev) => {
				const next = {
					...prev,
					page: "agents" as Page,
					agentName: agentName || null,
				};
				updateUrl(next);
				return next;
			});
		},
		[updateUrl],
	);

	// Listen for popstate events (browser back/forward)
	useEffect(() => {
		const handlePopState = () => {
			const params = new URLSearchParams(window.location.search);
			let page = (params.get("page") as Page) || "dashboard";
			if (window.location.pathname === "/register") {
				page = "register";
			}
			const callId = params.get("callId");
			const agentName = params.get("agentName");

			setState({ page, callId, agentName });
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	return {
		page: state.page,
		callId: state.callId,
		agentName: state.agentName,
		setPage,
		navigateToCall,
		navigateToAgent,
	};
}
